import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRoutineSetupChecklist } from '../contexts/RoutineSetupChecklistContext';
import { useToast } from '../contexts/ToastContext';
import { useMood } from '../theme/MoodContext';
import Typography from '../components/Text/Typography';
import Button from '../components/Button/Button';
import Toggle from "../components/Toggle/Toggle";
import NavigationTab from "../components/NavigationTab/NavigationTab";
import IconButton from "../components/Button/IconButton";
import Icon from "../components/Icon/Icon";
import Banner from "../components/Banner/Banner";
import {
  RoutineExplanationBanner,
  ExplanationByRoutineType,
  ROUTINE_EXPLANATION_STORAGE_KEY,
} from "../components/RoutineExplanationBanner/RoutineExplanationBanner";
import { RecommendationsBottomSheet } from "../bottomdrawers";
import RoutineCard from "../components/RoutineCard/RoutineCard";
import ActionRoutineCard from "../components/RoutineCard/ActionRoutineCard";
import ResponsiveDialog from "../components/ResponsiveDialog/ResponsiveDialog";
import EmptyCabinetState from '../components/EmptyCabinetState/EmptyCabinetState';
import "./RoutinePage.css";
import { API_BASE_URL, authenticatedFetch } from '../config/api';
import { guestDataService } from '../services/guestDataService';
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import Tooltip from "../components/Tooltip/Tooltip";
import { getActionById } from "../utils/actions";
import { Routine } from '../types/routine';

// Define the actual data structure returned from the API
interface RoutineProduct {
  routine_id: number;
  product_id: number;
  step_order: number;
  routine_type: string;
  day_of_week: string;
  created_at: string;
  product_name: string;
  brand: string;
  category: string;
  description: string;
  image_url: string;
  actives: string;
  texture: string;
  recommended_skin_type: string;
  targeted_skin_concerns: string;
  non_comedogenic: boolean;
  oil_free: boolean;
  alcohol_free: boolean;
  fragrance_free: boolean;
  paraben_free: boolean;
  vegan: boolean;
  cruelty_free: boolean;
  type?: 'product';
}

interface RoutineAction {
  actionId: string;
  actionName: string;
  description: string;
  icon: string;
  step_order: number;
  type: 'action';
}

type RoutineItem = RoutineProduct | RoutineAction;

interface MissingProduct {
  category: string;
  type: 'missing';
}

/** Server-returned validation warning for the current routine */
interface RoutineWarning {
  code: 'MISSING_REQUIRED_STEP' | 'OUT_OF_RECOMMENDED_ORDER' | 'ACTIVES_INCOMPATIBLE';
  routineType?: 'AM' | 'PM';
  message?: string;
  category?: string;
  step_order?: number;
  product_id?: string | number;
  other_product_id?: string | number;
  expected_position?: number;
}

// Type guards
const isRoutineProduct = (item: RoutineItem | MissingProduct): item is RoutineProduct => {
  return 'product_id' in item;
};

const isRoutineAction = (item: RoutineItem | MissingProduct): item is RoutineAction => {
  return 'actionId' in item;
};

const isMissingProduct = (item: RoutineItem | MissingProduct): item is MissingProduct => {
  return 'type' in item && item.type === 'missing';
};

const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Recommended category order from algorithm.js
const categoryOrder = {
  AM: [
    'cleanser',
    'toner',
    'facial mist',
    'serum',
    'treatment',
    'eye care',
    'all in one',
    'moisturizer',
    'sunscreen',
    'lip care'
  ],
  PM: [
    'cleanser',
    'toner',
    'facial mist',
    'face mask',
    'serum',
    'eye care',
    'treatment',
    'all in one',
    'moisturizer',
    'lip care'
  ]
};

const capitalizeWords = (str) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Map product category to order index (flexible match: "Hydrating Cleanser" -> cleanser slot, same as server algorithm)
function getCategoryOrderIndex(categoryStr, order) {
  if (!categoryStr || !order || order.length === 0) return -1;
  const c = categoryStr.toLowerCase().trim();
  for (let i = 0; i < order.length; i++) {
    const slot = order[i].toLowerCase().trim();
    if (c === slot || c.includes(slot) || slot.includes(c)) return i;
  }
  return -1;
}

// Resolve product type/category for order check (Contentful uses type; category may be empty; try types[] as fallback)
function getProductTypeOrCategory(product) {
  const candidates = [
    product.type,
    product.category,
    Array.isArray(product.types) && product.types[0]
  ].filter(Boolean);
  for (const c of candidates) {
    const s = String(c).trim();
    if (s) return s;
  }
  return null;
}

function getExpectedOrderIndex(product, order) {
  const typeOrCategory = getProductTypeOrCategory(product);
  if (!typeOrCategory) return -1;
  return getCategoryOrderIndex(typeOrCategory, order);
}

// Function to check if a product is in the correct order
const checkProductOrder = (product, currentIndex, routineType, allProducts) => {
  const order = categoryOrder[routineType];
  const expectedIndex = getExpectedOrderIndex(product, order);
  
  if (expectedIndex === -1) return { isCorrectOrder: true, expectedPosition: currentIndex + 1 };
  
  let hasOrderViolation = false;
  for (let i = 0; i < currentIndex; i++) {
    const previousProduct = allProducts[i];
    const previousExpectedIndex = previousProduct ? getExpectedOrderIndex(previousProduct, order) : -1;
    if (previousExpectedIndex !== -1 && previousExpectedIndex > expectedIndex) {
      hasOrderViolation = true;
      break;
    }
  }
  
  return { 
    isCorrectOrder: !hasOrderViolation, 
    expectedPosition: expectedIndex + 1 
  };
};

const NightRoutine = () => {
  const [selectedDay, setSelectedDay] = useState(0);
  const [routine, setRoutine] = useState<RoutineItem[]>([]);
  const [recommendations, setRecommendations] = useState<MissingProduct[]>([]);
  const [routineWarnings, setRoutineWarnings] = useState<RoutineWarning[]>([]);
  const [showRecommendations, setShowRecommendations] = useState(() => {
    // Initialize from localStorage; default to true so "Missing Required Products" banner appears when routine is incomplete
    const saved = localStorage.getItem('showRecommendations');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddToRoutineDialogOpen, setIsAddToRoutineDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<RoutineProduct | null>(null);
  const [daysWithoutActives, setDaysWithoutActives] = useState<Set<number>>(new Set());
  
  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Flag to track if routine is being loaded from server (to avoid saving to localStorage)
  const [isLoadingFromServer, setIsLoadingFromServer] = useState(false);
  
  const { user, isLoading, handleAuthError, signup } = useAuth();
  useMood('night');
  const { markVisitedPMRoutine } = useRoutineSetupChecklist();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [explanationByRoutineType, setExplanationByRoutineType] = useState<ExplanationByRoutineType>(() => {
    try {
      const stored = sessionStorage.getItem(ROUTINE_EXPLANATION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ExplanationByRoutineType;
        if (parsed && (parsed.AM || parsed.PM)) return parsed;
      }
    } catch (_) {
      // ignore
    }
    return { AM: null, PM: null };
  });

  // Restore explanation from cabinet "Update routines" (state or sessionStorage); never auto-dismiss
  useEffect(() => {
    const state = location.state as { routineExplanationByRoutineType?: ExplanationByRoutineType } | null;
    if (state?.routineExplanationByRoutineType) {
      setExplanationByRoutineType(state.routineExplanationByRoutineType);
      try {
        sessionStorage.setItem(ROUTINE_EXPLANATION_STORAGE_KEY, JSON.stringify(state.routineExplanationByRoutineType));
      } catch (_) {
        // ignore
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Rehydrate explanation from sessionStorage when user logs in (e.g. after logout → login) so banner persists
  useEffect(() => {
    if (!user?.id) return;
    if (explanationByRoutineType.AM || explanationByRoutineType.PM) return;
    try {
      const stored = sessionStorage.getItem(ROUTINE_EXPLANATION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ExplanationByRoutineType;
        if (parsed && (parsed.AM || parsed.PM)) setExplanationByRoutineType(parsed);
      }
    } catch (_) {
      // ignore
    }
  }, [user?.id]);

  // Mark routine setup checklist step 4 (Confirm Nighttime Routine) when user lands on this page
  useEffect(() => {
    markVisitedPMRoutine();
  }, [markVisitedPMRoutine]);

  // Update localStorage when showRecommendations changes (only for authenticated users)
  useEffect(() => {
    if (user) {
      localStorage.setItem('showRecommendations', JSON.stringify(showRecommendations));
    } else {
      // Reset to false for guests
      if (showRecommendations) {
        setShowRecommendations(false);
      }
    }
  }, [showRecommendations, user]);

  // Function to fetch missing required products (recommendations)
  const fetchRecommendations = async () => {
    if (!user?.id) {
      setRecommendations([]);
      return;
    }
    
    const dayOfWeek = days[selectedDay];
    try {
      const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}/recommendations`);
      if (response.ok) {
        const data = await response.json();
        // Ensure data is an array before setting it
        if (Array.isArray(data)) {
          setRecommendations(data);
        } else {
          console.error('❌ Recommendations API returned non-array data:', data);
          setRecommendations([]);
        }
      } else {
        setRecommendations([]);
      }
    } catch (error) {
      console.error('❌ Error fetching recommendations:', error);
      setRecommendations([]);
    }
  };

  useEffect(() => {
    const dayOfWeek = days[selectedDay];
    
    const loadRoutine = async () => {
      setIsLoadingFromServer(true);
      try {
        let serverRoutine: RoutineItem[] = [];
        if (user?.id) {
          try {
            const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
            if (response.ok) {
              const data = await response.json();
              serverRoutine = Array.isArray(data) ? data : (data.items || []);
              setRoutineWarnings(Array.isArray(data) ? [] : (data.warnings || []));
            }
          } catch (error) {
            console.error('Error fetching routine from API:', error);
            setRoutineWarnings([]);
          }
        }
        if (serverRoutine.length === 0) {
          serverRoutine = await guestDataService.getRoutine(user, 'PM', dayOfWeek) || [];
          if (serverRoutine.length > 0 && !user?.id) setRoutineWarnings([]);
        }
        
        // Check if user has custom reordering for this day
        const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username || 'guest'}`;
        const savedOrder = localStorage.getItem(key);
        
        if (savedOrder && serverRoutine && serverRoutine.length > 0) {
          try {
            const customOrder = JSON.parse(savedOrder);
            console.log(`NightRoutine: Found custom order for ${dayOfWeek}:`, customOrder.map((p) => ('product_id' in p ? p.product_name : p.actionName) ?? 'item'));
            // Apply custom order to the server routine (which now includes actions)
            const reorderedRoutine = applyCustomOrder(serverRoutine, customOrder);
            setRoutine(reorderedRoutine);
            console.log(`NightRoutine: Applied custom order for ${dayOfWeek}`);
          } catch (error) {
            console.error('Error applying custom order:', error);
            setRoutine(serverRoutine);
          }
        } else {
          console.log(`NightRoutine: No custom order found for ${dayOfWeek}, using server routine`);
          setRoutine(serverRoutine || []);
        }
      } catch (error) {
        console.error('Error loading routine:', error);
        setRoutine([]);
      } finally {
        setIsLoadingFromServer(false);
      }
    };
    
    loadRoutine();
    
    // Fetch recommendations if toggle is on and user is authenticated
    if (showRecommendations && user?.id) {
      fetchRecommendations();
    } else {
      setRecommendations([]);
    }
  }, [user, selectedDay, navigate, showRecommendations, isLoading]);

  // Automatically check for missing required products when routine changes
  useEffect(() => {
    if (showRecommendations && user?.id && !isLoadingFromServer) {
      fetchRecommendations();
    }
  }, [routine, showRecommendations, user, selectedDay]);

  // Check which days have no products with active ingredients
  useEffect(() => {
    const checkDaysForActives = async () => {
      const daysWithoutActivesSet = new Set<number>();
      
      for (let i = 0; i < days.length; i++) {
        const dayOfWeek = days[i];
        try {
          const dayRoutine = await guestDataService.getRoutine(user, 'PM', dayOfWeek);
          
          // If no routine exists and user is authenticated, try to fetch from API
          let routineToCheck = dayRoutine;
          if (user?.id && (!routineToCheck || routineToCheck.length === 0)) {
            try {
              const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
              if (response.ok) {
                const data = await response.json();
                routineToCheck = Array.isArray(data) ? data : (data.items || []);
              }
            } catch (error) {
              console.error(`Error fetching routine for ${dayOfWeek}:`, error);
            }
          }
          
          // Check if any product in the routine has active ingredients
          const hasActives = routineToCheck && routineToCheck.some((item: RoutineItem) => {
            // Check if it's a product (not an action) and has actives
            if (isRoutineProduct(item) && item.actives) {
              const activesStr = item.actives.trim();
              return activesStr.length > 0 && activesStr !== 'null' && activesStr !== 'NULL';
            }
            return false;
          });
          
          // If no products with actives, add this day to the set
          if (!hasActives) {
            daysWithoutActivesSet.add(i);
          }
        } catch (error) {
          console.error(`Error checking actives for ${dayOfWeek}:`, error);
        }
      }
      
      setDaysWithoutActives(daysWithoutActivesSet);
    };
    
    if (user !== undefined) {
      checkDaysForActives();
    }
  }, [user, days]);

  // Helper function to apply custom order to server routine
  const routineItemKey = (item: RoutineItem) =>
    item && 'product_id' in item && item.product_id != null
      ? `p:${String(item.product_id)}`
      : item && 'actionId' in item && item.actionId != null
        ? `a:${String(item.actionId)}`
        : null;

  const applyCustomOrder = (serverRoutine: RoutineItem[], customOrder: RoutineItem[]) => {
    const byKey = new Map<string, RoutineItem>();
    for (const item of serverRoutine || []) {
      const k = routineItemKey(item);
      if (k) byKey.set(k, item);
    }
    const customKeys = new Set<string>();
    const reordered: RoutineItem[] = [];
    let step = 1;
    for (const customItem of customOrder || []) {
      const k = routineItemKey(customItem);
      if (!k || !byKey.has(k)) continue;
      customKeys.add(k);
      const full = byKey.get(k)!;
      reordered.push({ ...full, step_order: step++ });
    }
    for (const item of serverRoutine || []) {
      const k = routineItemKey(item);
      if (k && !customKeys.has(k)) reordered.push({ ...item, step_order: step++ });
    }
    return reordered.sort((a, b) => (a.step_order ?? 0) - (b.step_order ?? 0));
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Merge routine and recommendations (missing products)
  const visibleProducts = showRecommendations
    ? [...(Array.isArray(routine) ? routine : []), ...(Array.isArray(recommendations) ? recommendations : [])]
    : (Array.isArray(routine) ? routine : []);

  // Check if there are missing required categories (from recommendations API or server warnings; only show warnings for this routine type PM)
  const missingFromWarnings = routineWarnings
    .filter((w) => w.code === 'MISSING_REQUIRED_STEP' && w.routineType === 'PM')
    .map((w) => capitalizeWords(w.category || ''));
  const missingRequiredCategories = showRecommendations && (
    (Array.isArray(recommendations) && recommendations.length > 0) ||
    missingFromWarnings.length > 0
  );

  // Get list of missing categories for the warning banner (prefer server warnings when present)
  const missingCategories = showRecommendations && missingFromWarnings.length > 0
    ? missingFromWarnings
    : (Array.isArray(recommendations) ? recommendations.map(rec => capitalizeWords(rec.category)) : []);

  // Out-of-order warning for a specific product (from server warnings, with client fallback)
  const getOrderWarningForProduct = (item: RoutineItem, idx: number) => {
    if (!isRoutineProduct(item)) return { show: false, expectedPosition: undefined };
    const w = routineWarnings.find(
      (x) => x.code === 'OUT_OF_RECOMMENDED_ORDER' && x.routineType === 'PM' && String(x.product_id) === String(item.product_id)
    );
    if (w) return { show: true, expectedPosition: w.expected_position };
    const fallback = checkProductOrder(item, idx, 'PM', routine);
    return { show: !fallback.isCorrectOrder, expectedPosition: fallback.expectedPosition };
  };

  const hasActivesIncompatibleWarning = routineWarnings.some((w) => w.code === 'ACTIVES_INCOMPATIBLE' && w.routineType === 'PM');

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.outerHTML);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Only show drag over indicator if we're dragging over a different position
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Since we're only dealing with actual routine items now, no need to check for missing products

    // Get the dragged item from routine array
    const draggedItem = routine[draggedIndex];
    if (!draggedItem) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Create new array with reordered items
    const newRoutine = [...routine];
    const draggedRoutineIndex = routine.findIndex(item => {
      if (isRoutineProduct(item) && isRoutineProduct(draggedItem)) {
        return item.product_id === draggedItem.product_id;
      }
      if (isRoutineAction(item) && isRoutineAction(draggedItem)) {
        return item.actionId === draggedItem.actionId;
      }
      return false;
    });
    
    if (draggedRoutineIndex !== -1) {
      newRoutine.splice(draggedRoutineIndex, 1);
      const dropRoutineIndex = Math.min(dropIndex, routine.length);
      newRoutine.splice(dropRoutineIndex, 0, draggedItem);
      setRoutine(newRoutine);
      
      // Save the new order using guestDataService
      const dayOfWeek = days[selectedDay];
      if (!isLoadingFromServer) {
        setIsSaving(true);
        console.log('Saving routine order:', { dayOfWeek, routine: newRoutine });
        
        // Save to localStorage immediately for user customizations
        const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username || 'guest'}`;
        const currentOrder = newRoutine.map((p, index) => ({ ...p, step_order: index + 1 }));
        localStorage.setItem(key, JSON.stringify(currentOrder));
        
      // Save using guestDataService (handles both guest and authenticated)
      guestDataService.saveRoutine(user, 'PM', dayOfWeek, newRoutine)
        .then(success => {
          if (success) {
            console.log('Routine order saved successfully');
            // Update recommendations after reordering
            if (showRecommendations && user?.id) {
              fetchRecommendations();
            }
          } else {
            console.warn('Failed to save routine order');
          }
          setIsSaving(false);
        })
        .catch(error => {
          console.error('Error saving routine order:', error);
          setIsSaving(false);
        });
      }
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleAddProductToRoutine = async (productId) => {
    // Refresh the routine data after adding a product
    const dayOfWeek = days[selectedDay];
    if (user) {
      try {
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
        if (response.ok) {
          const data = await response.json();
          const serverRoutine = Array.isArray(data) ? data : (data.items || []);
          setRoutineWarnings(Array.isArray(data) ? [] : (data.warnings || []));
          
          // Check if user has custom reordering for this day
          const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username}`;
          const savedOrder = localStorage.getItem(key);
          
          if (savedOrder) {
            try {
              const customOrder = JSON.parse(savedOrder);
              const reorderedRoutine = applyCustomOrder(serverRoutine, customOrder);
              setRoutine(reorderedRoutine);
            } catch (error) {
              console.error('Error applying custom order:', error);
              setRoutine(serverRoutine);
            }
          } else {
            setRoutine(serverRoutine);
          }
          
          // Update recommendations after adding product
          if (showRecommendations) {
            fetchRecommendations();
          }
        }
      } catch (error) {
        console.error('Error refreshing routine after adding product:', error);
      }
    }
  };

  const handleAddProductToCabinet = async (productId: number) => {
    try {
      const success = await guestDataService.addProduct(user, productId);
      if (success) showToast('Product added to cabinet');
      else showToast('Product is already in your cabinet', 'info');
    } catch {
      showToast('Failed to add product', 'error');
    }
  };

  const handleAddActionToRoutine = async (actionId) => {
    const dayOfWeek = days[selectedDay];
    if (user) {
      try {
        // Add the action to the routine via API
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}/actions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            actionId: actionId
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to add action to routine');
        }

        // Refresh the routine data after adding the action
        const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const serverRoutine = Array.isArray(data) ? data : (data.items || []);
          setRoutineWarnings(Array.isArray(data) ? [] : (data.warnings || []));
          
          // Check if user has custom reordering for this day
          const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username}`;
          const savedOrder = localStorage.getItem(key);
          
          if (savedOrder) {
            try {
              const customOrder = JSON.parse(savedOrder);
              const reorderedRoutine = applyCustomOrder(serverRoutine, customOrder);
              setRoutine(reorderedRoutine);
            } catch (error) {
              console.error('Error applying custom order:', error);
              setRoutine(serverRoutine);
            }
          } else {
            setRoutine(serverRoutine);
          }
          
          // Update recommendations after adding action
          if (showRecommendations) {
            fetchRecommendations();
          }
        }
      } catch (error) {
        console.error('Error adding action to routine:', error);
        alert(`Failed to add action to routine: ${error.message}`);
      }
    }
  };

  const handleDeleteProductFromRoutine = async (productId) => {
    const dayOfWeek = days[selectedDay];
    if (user) {
      try {
        // Remove the product from the routine via API
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}/products/${productId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove product from routine');
        }

        // Refresh the routine data after removing the product
        const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const serverRoutine = Array.isArray(data) ? data : (data.items || []);
          setRoutineWarnings(Array.isArray(data) ? [] : (data.warnings || []));
          
          // Check if user has custom reordering for this day
          const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username}`;
          const savedOrder = localStorage.getItem(key);
          
          if (savedOrder) {
            try {
              const customOrder = JSON.parse(savedOrder);
              // Remove the deleted product from custom order
              const updatedCustomOrder = customOrder.filter((p) => p.product_id !== productId);
              localStorage.setItem(key, JSON.stringify(updatedCustomOrder));
              
              const reorderedRoutine = applyCustomOrder(serverRoutine, updatedCustomOrder);
              setRoutine(reorderedRoutine);
            } catch (error) {
              console.error('Error applying custom order:', error);
              setRoutine(serverRoutine);
            }
          } else {
            setRoutine(serverRoutine);
          }
          
          // Update recommendations after removing product
          if (showRecommendations) {
            fetchRecommendations();
          }
        }
      } catch (error) {
        console.error('Error removing product from routine:', error);
        alert(`Failed to remove product from routine: ${error.message}`);
      }
    }
  };

  const handleDeleteActionFromRoutine = async (actionId) => {
    const dayOfWeek = days[selectedDay];
    if (user) {
      try {
        // Remove the action from the routine via API
        const response = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}/actions/${actionId}`, {
          method: 'DELETE',
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to remove action from routine');
        }

        // Refresh the routine data after removing the action
        const refreshResponse = await authenticatedFetch(`${API_BASE_URL}/api/user/routines/PM/${dayOfWeek}`);
        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const serverRoutine = Array.isArray(data) ? data : (data.items || []);
          setRoutineWarnings(Array.isArray(data) ? [] : (data.warnings || []));
          
          // Check if user has custom reordering for this day
          const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username}`;
          const savedOrder = localStorage.getItem(key);
          
          if (savedOrder) {
            try {
              const customOrder = JSON.parse(savedOrder);
              // Remove the deleted action from custom order
              const updatedCustomOrder = customOrder.filter((item) => item.actionId !== actionId);
              localStorage.setItem(key, JSON.stringify(updatedCustomOrder));
              
              const reorderedRoutine = applyCustomOrder(serverRoutine, updatedCustomOrder);
              setRoutine(reorderedRoutine);
            } catch (error) {
              console.error('Error applying custom order:', error);
              setRoutine(serverRoutine);
            }
          } else {
            setRoutine(serverRoutine);
          }
          
          // Update recommendations after removing action
          if (showRecommendations) {
            fetchRecommendations();
          }
        }
      } catch (error) {
        console.error('Error removing action from routine:', error);
        alert(`Failed to remove action from routine: ${error.message}`);
      }
    }
  };

  // Mobile-friendly reordering handlers
  const moveItemUp = (index) => {
    if (index === 0) return; // Can't move first item up
    
    const newRoutine = [...routine];
    const item = newRoutine[index];
    newRoutine.splice(index, 1);
    newRoutine.splice(index - 1, 0, item);
    setRoutine(newRoutine);
    
    // Save the new order using guestDataService
    const dayOfWeek = days[selectedDay];
    if (!isLoadingFromServer) {
      setIsSaving(true);
      console.log('Saving routine order (mobile):', { dayOfWeek, routine: newRoutine });
      
      // Save to localStorage immediately for user customizations
      const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username || 'guest'}`;
      const currentOrder = newRoutine.map((p, index) => ({ ...p, step_order: index + 1 }));
      localStorage.setItem(key, JSON.stringify(currentOrder));
      
      // Save using guestDataService (handles both guest and authenticated)
      guestDataService.saveRoutine(user, 'PM', dayOfWeek, newRoutine)
        .then(success => {
          if (success) {
            console.log('Routine order saved successfully (mobile)');
            // Update recommendations after reordering
            if (showRecommendations && user?.id) {
              fetchRecommendations();
            }
          } else {
            console.warn('Failed to save routine order (mobile)');
          }
          setIsSaving(false);
        })
        .catch(error => {
          console.error('Error saving routine order (mobile):', error);
          setIsSaving(false);
        });
    }
  };

  const moveItemDown = (index) => {
    if (index === routine.length - 1) return; // Can't move last item down
    
    const newRoutine = [...routine];
    const item = newRoutine[index];
    newRoutine.splice(index, 1);
    newRoutine.splice(index + 1, 0, item);
    setRoutine(newRoutine);
    
    // Save the new order using guestDataService
    const dayOfWeek = days[selectedDay];
    if (!isLoadingFromServer) {
      setIsSaving(true);
      console.log('Saving routine order (mobile):', { dayOfWeek, routine: newRoutine });
      
      // Save to localStorage immediately for user customizations
      const key = `routineOrder_PM_${dayOfWeek}_${user?.id || user?.username || 'guest'}`;
      const currentOrder = newRoutine.map((p, index) => ({ ...p, step_order: index + 1 }));
      localStorage.setItem(key, JSON.stringify(currentOrder));
      
      // Save using guestDataService (handles both guest and authenticated)
      guestDataService.saveRoutine(user, 'PM', dayOfWeek, newRoutine)
        .then(success => {
          if (success) {
            console.log('Routine order saved successfully (mobile)');
            // Update recommendations after reordering
            if (showRecommendations && user?.id) {
              fetchRecommendations();
            }
          } else {
            console.warn('Failed to save routine order (mobile)');
          }
          setIsSaving(false);
        })
        .catch(error => {
          console.error('Error saving routine order (mobile):', error);
          setIsSaving(false);
        });
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading..." />;
  }

  return (
    <div className="routine-page-container">
      <div className="routine-header">
        <div className="routine-header-left">
          <IconButton
            icon={<Icon name="chevron-left" size="large" />}
            onClick={() => navigate('/')}
            ariaLabel="Back to home"
            className="routine-back-button"
          />
          <div className="routine-header-copy">
            <Typography variant="h1">Nighttime Routine</Typography>
            {isSaving && (
              <div className="saving-indicator">
                <Icon name="refresh" size="small" className="spinning" />
                <Typography variant="body-small">Saving...</Typography>
              </div>
            )}
          </div>
        </div>
        {isMobile ? (
          <IconButton
            icon={<Icon name="adjustments" size="large" />}
            onClick={() => setIsDrawerOpen(true)}
            ariaLabel="Adjust routine settings"
            className="recommendations-icon-button"
          />
        ) : (
          user ? (
            <Toggle
              checked={showRecommendations}
              onChange={setShowRecommendations}
              label="Show recommendations"
              id="show-recommendations"
            />
          ) : (
            <Tooltip text="Create account to access feature" position="top">
              <div style={{ display: 'inline-block' }}>
                <Toggle
                  checked={false}
                  onChange={() => {}}
                  label="Show recommendations"
                  id="show-recommendations"
                  disabled={true}
                />
              </div>
            </Tooltip>
          )
        )}
      </div>
      {/* Guest marketing banner above tabs (only for non-logged-in users) */}
      {!user?.id && (
        <div className="guest-marketing-banner">
          <div className="guest-marketing-banner-text">
            <Typography variant="body-small-bold" className="guest-marketing-banner-title">
              Create an account to unlock the full Dew experience
            </Typography>
            <Typography variant="body-small" className="guest-marketing-banner-body">
              Get smarter routines with ingredient safety checks, frequency scheduling, and personalized explanations for every night of the week.
            </Typography>
          </div>
          <Button variant="primary" onClick={signup}>
            Create account
          </Button>
        </div>
      )}
      {/* Expandable "Why this routine?" banner above day tabs; persists, no auto-dismiss (only for logged-in users) */}
      {user?.id && explanationByRoutineType.PM && (
        <div className="recommendations-banner-container routine-explanation-banner">
          <RoutineExplanationBanner
            routineType="PM"
            data={explanationByRoutineType.PM}
          />
        </div>
      )}
      <div className="routine-tabs">
        <NavigationTab
          days={days}
          selected={selectedDay}
          onSelect={setSelectedDay}
        />
      </div>
      <div className="routine-content">
        {/* Warning banner for missing required categories */}
        {!isLoadingFromServer && missingRequiredCategories && (
          <div className="recommendations-banner-container">
            <Banner variant="warning" title="Missing Required Products">
              Your routine is missing some essential products: {missingCategories.map((category, index) => (
                <React.Fragment key={category}>
                  <Typography variant="body-small-bold" style={{ display: 'inline' }}>
                    {category}
                  </Typography>
                  {index < missingCategories.length - 1 ? ', ' : ''}
                </React.Fragment>
              ))}. Consider adding these to complete your skincare routine.
            </Banner>
          </div>
        )}
        {/* Warning banner for incompatible actives */}
        {!isLoadingFromServer && hasActivesIncompatibleWarning && (
          <div className="recommendations-banner-container">
            <Banner variant="caution" title="Incompatible Actives">
              Some products in your routine contain actives that should not be used together. Consider spacing them on different days or removing one.
            </Banner>
          </div>
        )}
        
        {routine.length > 0 ? (
          routine.map((item, idx) => {
            const isRoutineItem = true; // All items in routine are actual routine items
            const isDragging = draggedIndex === idx;
            const isDragOver = dragOverIndex === idx;
            
            // Only render actual routine items, not missing products
            
            // Render as RoutineCard for products or ActionRoutineCard for actions
            return (
              <div
                key={isRoutineProduct(item) ? item.product_id : item.actionId}
                className={`routine-card-wrapper ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${!isRoutineItem ? 'non-draggable' : ''}`}
                onDragOver={(e) => isRoutineItem && handleDragOver(e, idx)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => isRoutineItem && handleDrop(e, idx)}
              >
                {isRoutineAction(item) ? (
                  // Render ActionRoutineCard for actions
                  <ActionRoutineCard
                    actionId={item.actionId}
                    actionName={item.actionName || getActionById(item.actionId)?.name || 'Action'}
                    description={item.description || getActionById(item.actionId)?.description || ''}
                    onDelete={() => handleDeleteActionFromRoutine(item.actionId)}
                    onDragStart={isRoutineItem ? (e) => handleDragStart(e, idx) : undefined}
                    onDragEnd={handleDragEnd}
                    onMoveUp={isRoutineItem ? () => moveItemUp(idx) : undefined}
                    onMoveDown={isRoutineItem ? () => moveItemDown(idx) : undefined}
                    canMoveUp={isRoutineItem && idx > 0}
                    canMoveDown={isRoutineItem && idx < routine.length - 1}
                    showMobileControls={isMobile && isRoutineItem}
                    currentPosition={idx + 1}
                  />
                ) : (
                  // Render RoutineCard for products
                  <RoutineCard
                    company={isRoutineProduct(item) ? item.brand : ''}
                    productName={isRoutineProduct(item) ? item.product_name : ''}
                    category={isRoutineProduct(item) ? (item.category || item.type || '') : ''}
                    imageSrc={isRoutineProduct(item) ? item.image_url : undefined}
                    onDelete={isRoutineProduct(item) ? () => handleDeleteProductFromRoutine(item.product_id) : undefined}
                    onDragStart={isRoutineItem ? (e) => handleDragStart(e, idx) : undefined}
                    onDragEnd={handleDragEnd}
                    onMoveUp={isRoutineItem ? () => moveItemUp(idx) : undefined}
                    onMoveDown={isRoutineItem ? () => moveItemDown(idx) : undefined}
                    canMoveUp={isRoutineItem && idx > 0}
                    canMoveDown={isRoutineItem && idx < routine.length - 1}
                    showMobileControls={isMobile && isRoutineItem}
                    showOrderWarning={showRecommendations && isRoutineItem && getOrderWarningForProduct(item, idx).show}
                    expectedPosition={getOrderWarningForProduct(item, idx).expectedPosition}
                    currentPosition={idx + 1}
                    product={isRoutineProduct(item) ? item : undefined}
                    onClick={isRoutineProduct(item) ? setSelectedProduct : undefined}
                  />
                )}
              </div>
            );
          })
        ) : (
          <EmptyCabinetState
            title="Your nighttime routine is empty"
            description="Add products from your cabinet to build a routine tailored to your skin."
            actionLabel="Add to routine"
            onAction={() => setIsAddToRoutineDialogOpen(true)}
          />
        )}
      </div>
      
      <RecommendationsBottomSheet
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        showRecommendations={showRecommendations}
        onToggleChange={setShowRecommendations}
      />

      {/* Add to Routine Button — the empty state carries its own call to action */}
      {routine.length > 0 && (
        <div className="add-to-routine-button-container">
          <Button
            variant="text"
            onClick={() => setIsAddToRoutineDialogOpen(true)}
            className="add-to-routine-button"
            icon={{ name: "add", size: "small" }}
          >
            Add to Routine
          </Button>
        </div>
      )}

      {/* Add to Routine Dialog */}
      <ResponsiveDialog
        type="addToRoutine"
        isOpen={isAddToRoutineDialogOpen}
        onClose={() => setIsAddToRoutineDialogOpen(false)}
        onAddProduct={handleAddProductToRoutine}
        onAddAction={handleAddActionToRoutine}
        routineType="PM"
        selectedDay={days[selectedDay]}
        currentRoutineProducts={routine}
        userId={user?.id || 0}
      />

      {/* Product detail (from routine card click) */}
      <ResponsiveDialog
        type="productDetail"
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        product={selectedProduct ?? undefined}
        onAddProductToCabinet={handleAddProductToCabinet}
      />
    </div>
  );
};

export default NightRoutine;
